import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import Proptypes from "prop-types";

import { getThemeClasses } from "@utils/bestInTravel/2025/getThemeClasses";
import { useScreens } from "@utils/component";
import data from "@constants/best-in-travel/2025";

// CONTEXT DEFAULT VALUES
const BestInTravelContext = createContext({
  destinations: [],

  theme: "countries",
  themeClasses: {},
  setTheme: () => {},

  handleScrollToMap: () => {},

  shareDrawerOpen: false,
  setShareDrawerOpen: () => {},
  shareData: null,
  setShareData: () => {},
  handleShareDrawer: () => {},

  destinationsMenuOpen: false,
  setDestinationsMenuOpen: () => {},

  handleUpDownNavProvider: () => {},
  sectionRefs: [],

  videoOpen: false,
  video: null,
  handleVideo: () => {},
  closeVideoModal: () => {},
  setVideoOpen: () => {},

  markerId: null,
  setMarkerId: () => {},

  screen: null,

  loading: true,
  setLoading: () => {},

  isClient: false,
});

// PROVIDER COMPONENT
export function BestInTravelProvider({ children }) {
  // DATA
  const destinations = data?.destinations;

  // THEMES
  const [theme, setTheme] = useState("countries"); // Default theme
  const themeClasses = useMemo(() => getThemeClasses(theme), [theme]);

  // SCROLL TO MAP SECTION
  const mapSectionRef = useRef(null);
  const handleScrollToMap = useCallback(() => {
    if (mapSectionRef.current) {
      mapSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [mapSectionRef]);

  // SHARE DRAWER
  const [shareDrawerOpen, setShareDrawerOpen] = useState(false);
  const [shareData, setShareData] = useState(null);
  const handleShareDrawer = useCallback(destination => {
    setShareData(destination);
    setShareDrawerOpen(true);
  }, []);

  // DESTINATIONS NAVIGATION
  const [destinationsMenuOpen, setDestinationsMenuOpen] = useState(false);

  // UP AND DOWN NAVIGATION
  const sectionRefs = useRef([]);
  const handleUpDownNavProvider = useCallback(
    (index, direction) => {
      const newIndex = index + direction;

      if (newIndex >= 0 && newIndex < sectionRefs.current.length) {
        sectionRefs.current[newIndex].scrollIntoView({ behavior: "smooth" });
      }
    },
    [sectionRefs]
  );

  // SCROLLING EFFECTS
  useEffect(() => {
    // prevent scrolling when share drawer or destination nav is open
    const body = document.querySelector("body");
    const classes = "overflow-y-hidden";
    if (shareDrawerOpen || destinationsMenuOpen) {
      body.classList.add(classes);
    }
    if (!shareDrawerOpen && !destinationsMenuOpen) {
      body.classList.remove(classes);
    }
  }, [shareDrawerOpen, destinationsMenuOpen]);

  // PLAY VIDEO NAVIGATION
  const [videoOpen, setVideoOpen] = useState(false);
  const [video, setVideo] = useState(null);
  const handleVideo = useCallback(obj => {
    setVideoOpen(true);
    setVideo(obj);
  }, []);
  const closeVideoModal = useCallback(() => {
    setVideoOpen(false);
    setVideo(null); // Reset the video data when closing
  }, []);

  // MAP MARKERS
  const [markerId, setMarkerId] = useState(null);

  // SCREEN SIZE
  const { screen } = useScreens();

  // LOADING
  const [loading, setLoading] = useState(true);

  // CLIENT-SIDE DETECTION
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Context Values
  const values = useMemo(
    () => ({
      destinations,

      theme,
      themeClasses,
      setTheme,

      handleScrollToMap,
      mapSectionRef,

      shareDrawerOpen,
      setShareDrawerOpen,
      shareData,
      setShareData,
      handleShareDrawer,

      destinationsMenuOpen,
      setDestinationsMenuOpen,

      handleUpDownNavProvider,
      sectionRefs,

      videoOpen,
      video,
      handleVideo,
      setVideoOpen,
      closeVideoModal,

      markerId,
      setMarkerId,

      screen,

      loading,
      setLoading,

      isClient,
    }),
    [
      destinations,
      theme,
      themeClasses,
      handleScrollToMap,
      shareDrawerOpen,
      shareData,
      handleShareDrawer,
      destinationsMenuOpen,
      handleUpDownNavProvider,
      sectionRefs, // Note: sectionRefs.current is mutable and won't cause re-memoization
      video,
      videoOpen,
      handleVideo,
      closeVideoModal,
      markerId,
      screen,
      loading,
      isClient,
    ]
  );

  return (
    <BestInTravelContext.Provider value={values}>
      {children}
    </BestInTravelContext.Provider>
  );
}

BestInTravelProvider.propTypes = {
  children: Proptypes.node.isRequired,
};

// Custom hook to use the context
export const useBestInTravel = () => useContext(BestInTravelContext);
